import { Namespace, TFunction } from 'react-i18next';
import {
  compareAsc,
  compareDesc,
  differenceInDays,
  differenceInHours,
  format,
  isToday,
  isYesterday,
  parseISO,
} from 'date-fns';
import i18n from 'i18next';

import { Dialog, Message } from 'api/DialogsApi';

export const getUTCDate = (dateString = Date.now()) => {
  const date = new Date(dateString);

  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
};

export const parseDateToSafari = (dateString: string) =>
  dateString.replace(/-/g, '/');

export const addUTCToDate = (dateString: string) => `${dateString} UTC`;

export const formatThreadDate = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  const parsedDate = new Date(addUTCToDate(parseDateToSafari(dateString)));

  if (isToday(parsedDate)) {
    return i18n.t('app:thread.dates.todayWithDate', {
      date: format(parsedDate, 'HH:mm'),
    });
  }

  if (isYesterday(parsedDate)) {
    return i18n.t('app:thread.dates.yesterdayWithDate', {
      date: format(parsedDate, 'HH:mm'),
    });
  }

  return format(parsedDate, 'dd MMM yyyy, HH:mm');
};

export const formatThreadDateSimpleDates = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  const parsedDate = new Date(addUTCToDate(parseDateToSafari(dateString)));

  if (isToday(parsedDate)) {
    return i18n.t('app:thread.dates.today');
  }

  if (isYesterday(parsedDate)) {
    return i18n.t('app:thread.dates.yesterday');
  }

  return format(parsedDate, 'dd MMMM yyyy');
};

export const formatThreadTime = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  const parsedDate = new Date(addUTCToDate(parseDateToSafari(dateString)));

  return format(parsedDate, 'HH:mm');
};

export const formatDialogDate = (dateString: string) => {
  if (!dateString) {
    return '';
  }

  const parsedDate = new Date(addUTCToDate(parseDateToSafari(dateString)));

  if (isToday(parsedDate)) {
    return format(parsedDate, 'HH:mm');
  }

  if (isYesterday(parsedDate)) {
    return i18n.t('app:thread.dates.yesterday');
  }

  return format(parsedDate, 'dd.MM.yyyy');
};

export const groupMessagesByDate = (messages: Message[]) => {
  const toClusteredByDays = (
    acc: Record<string, Message[]>,
    message: Message
  ) => {
    const DATE_FORMAT = 'yyyy-MM-dd';
    const date = format(parseISO(message.sent_at), DATE_FORMAT);

    if (acc[date]) {
      acc[date].push(message);
    } else {
      acc[date] = [message];
    }

    return acc;
  };

  return messages.reduce(toClusteredByDays, {});
};

export const sortDialogsByDate = (dialogs: Dialog[]) => {
  if (!Array.isArray(dialogs)) {
    return [];
  }

  return [...dialogs].sort((a, b) => {
    const dateA = parseDateToSafari(a.last_message.sent_at);
    const dateB = parseDateToSafari(b.last_message.sent_at);

    return compareDesc(new Date(dateA), new Date(dateB));
  });
};

export const sortMessagesByDate = (messages: Message[]) => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return [...messages].sort((a, b) => {
    const dateA = parseDateToSafari(a.sent_at);
    const dateB = parseDateToSafari(b.sent_at);

    return compareAsc(new Date(dateA), new Date(dateB));
  });
};

export const formatSubscriptionTime = (
  expiredTime: string,
  t: TFunction<Namespace>
) => {
  const expiredAt = new Date(expiredTime);
  const now = new Date();
  const daysLeft = differenceInDays(expiredAt, now);
  const hoursLeft = differenceInHours(expiredAt, now);

  return {
    timeLeftText:
      daysLeft > 0
        ? `${daysLeft} ${
            daysLeft === 1
              ? t('payment.unsubscribe.day')
              : t('payment.unsubscribe.days')
          }`
        : `${hoursLeft} ${
            hoursLeft === 1
              ? t('payment.unsubscribe.hour')
              : t('payment.unsubscribe.hours')
          }`,
    formattedDate: format(expiredAt, 'd MMM yyyy'),
  };
};
